import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';

import { Button } from '../../shared/ui/button';
import { ConfirmDelete } from '../../shared/ui/confirm-delete';
import { DangerAction } from '../../shared/ui/danger-zone';
import { MutationError } from '../../shared/ui/mutation-error';
import { deleteOrganization, type TOrganization } from '../../entities/organization';
import { moveOutOf } from '../../entities/session';

interface IOrganizationDeleteProps {
  organization: TOrganization;
  owner: boolean;
}

export const OrganizationDelete = ({ organization, owner }: IOrganizationDeleteProps) => {
  const [confirming, setConfirming] = useState(false);
  const client = useQueryClient();
  const navigate = useNavigate();

  const remove = useMutation({
    mutationFn: async () => {
      await deleteOrganization(organization.name);
      return moveOutOf(organization.id);
    },
    onSuccess: async (where) => {
      client.clear();
      await navigate({ to: where === 'switched' ? '/' : '/sign-in' });
    },
  });

  return (
    <>
      <DangerAction
        title="Delete this organization"
        blocked={owner ? null : 'Only an owner can delete the organization.'}
        action={
          <Button
            size="sm"
            variant="secondary"
            destructive
            disabled={!owner}
            onClick={() => setConfirming(true)}
          >
            Delete organization
          </Button>
        }
      >
        Removes {organization.name} for everyone: its flows, sources, destinations, stored requests,
        members and API keys. Turn off every source and delete every destination first.
      </DangerAction>

      <ConfirmDelete
        open={confirming}
        onOpenChange={(open) => {
          setConfirming(open);
          if (!open) remove.reset();
        }}
        entity="organization"
        name={organization.name}
        consequences="everything in it, for every member"
        note={remove.isError ? <MutationError error={remove.error} /> : undefined}
        waiting={remove.isPending}
        onConfirm={() => remove.mutate()}
      />
    </>
  );
};
